import {
    ConfigBlockInterface,
    PageTopActionsConfigInterface,
} from 'components/interfaces/GeneralInterface';
import { DisciplineEnum } from 'types/types';
import { ClubReferenceInterface } from 'components/interfaces/ClubInterface';
import {
    FootballStateEnum,
    GenderEnum,
    MatchApiInterface,
    MemberDetailApiInterface,
    PaginatorInterface,
    ClubPersonRolesApiEnum,
    PersonDetailsApiInterface,
    PersonReferenceApiInterface,
    MemberPlayerListApiInterface,
    AddressApiInterface,
    PlayerDetailApiInterface,
    FutsalStateEnum,
    ConfigModuleApiInterface,
    InternalUserDetailApiInterface,
    RefereeDelegateApiInterface,
    TagApiInterface,
} from 'submodules/api_middleware';

export interface MoreInfoInterface {
    birth_city: string;
    birth_country_code: string;
    language: string;
    second_citizen_of: string;
    citizen_of: string;
}

export interface ChargeInterface {
    from: string;
    to: string;
    amount: string;
    note: string;
}

export interface ContractInterface {
    id: string;
    is_active: boolean;
    is_professional: boolean;
    club: ClubReferenceInterface;
    type: string;
    valid_from: string;
    valid_to: string;
    signed_date: string;
    inserted_date: string;
    option_date: string;
    archived_at: string;
    terminated_date: string;
    created_at: string;
    agent?: PersonReferenceApiInterface;
    agentId: number;
    agentName: string;
    note?: string;
}

export interface LicenceInterface {
    id: string;
    role: string;
    league: string;
    licenceType: string;
    from: string;
    to: string;
}

export interface PassportInterface {
    type: string;
    club: string;
    country: string;
    from: string;
    to: string;
    isProfi: boolean;
}

export interface MembershipDetailsBEInterface {
    id: number;
    person_id: number;
    discipline: string;
    is_valid: boolean;
    valid_from: string;
    valid_to: string;
    amount: number;
    note: string;
}

export interface MembershipInterface {
    charges: MembershipDetailsBEInterface[];
    status: string;
}

export interface PenalizationInterface {
    id: string;
}

export interface MemberDetailsInterface {
    internalId: string;
}

export interface PlayerDetailsInterface {
    registeredInClub?: PlayerClubInterface;
    hostingInClub?: PlayerClubInterface;
    playerIndices: PlayerIndicesInterface;
}

export interface PlayerClubInterface {
    id: number;
    club: string;
    from?: string;
    to?: string;
}

export interface PlayerIndicesInterface {
    isFree?: boolean;
    isRequested?: boolean;
    isProfi?: boolean;
    isForeign?: boolean;
    isRequest?: boolean;
}

export interface PlayerContractInterface extends ContractInterface {
    has_dispute: boolean;
    archived_at: string;
    updated_at: string;
    updated_by: {
        person_id: string;
        name: string;
    };
    created_by: {
        person_id: string;
        name: string;
    };
    option_date: string;
}

export interface PersonToAddInterface extends MemberPlayerListApiInterface {
    amount?: number;
    club_roles?: ClubPersonRolesApiEnum[];
    licence?: LicenceInterface;
    actions?: PersonToAddActionsInterface;
    user?: InternalUserDetailApiInterface;
    referee?: RefereeDelegateApiInterface;
    delegate?: RefereeDelegateApiInterface;
}

export interface PersonToAddActionsInterface {
    assign_match_person: ConfigModuleApiInterface;
}

export interface PersonsToAddInterface {
    pagination: PaginatorInterface;
    data: PersonToAddInterface[];
}

export interface PersonInterface {
    id: string;
    morePersonDetails?: MoreInfoInterface;
    personAddress?: AddressApiInterface;
    personAddresses?: AddressApiInterface[];
    memberDetails?: MemberDetailsInterface;
    playerDetails?: PlayerDetailsInterface;
    membershipDetails?: MembershipInterface;
    profileColors: string[];
    tags?: TagApiInterface[];
    charge?: ChargeInterface;
    matches?: MatchApiInterface[];
    penalizations?: PenalizationInterface[];
}

export interface PersonBEInterface {
    first_name: string;
    last_name: string;
    gender: GenderEnum;
    birth_date: string;
    birth_country_code: string;
    national_identifier: string;
}

interface IdNameObjectInterface {
    id?: number;
    name?: string;
}

export interface PlayerRegisteredInInterface {
    registered: IdNameObjectInterface;
    hosting: IdNameObjectInterface;
}

export interface PlayerObjectBEInterface {
    football: PlayerRegisteredInInterface;
    futsal: PlayerRegisteredInInterface;
    competition: PlayerRegisteredInInterface;
}

export interface OrganisationUnitInterface extends PersonInterface {
    id: string;
    name: string;
}

export interface RefereeInterface extends PersonInterface {
    organisationUnit?: OrganisationUnitInterface;
    matches?: MatchApiInterface[];
    licence?: LicenceInterface;
    licences?: LicenceInterface[];

    licence_rank?: string;
    next_match_date?: string;
}

export enum PersonPageTopActionsEnum {
    'export_gdpr' = 'export_gdpr',
    'print_cover' = 'print_cover',
    'create_contract' = 'create_contract',
    'add-function' = 'add-function',
    'register-player' = 'register-player',
    'transfer' = 'transfer',
    'request-from-abroad' = 'request-from-abroad',
    'player_sub_hosting_termination' = 'player_sub_hosting_termination',
    'player_registration' = 'player_registration',
    'international_player_transfer' = 'international_player_transfer',
    'amateur_player_transfer' = 'amateur_player_transfer',
    'professional_player_sub_hosting' = 'professional_player_sub_hosting',
    'player_sub_hosting_extension' = 'player_sub_hosting_extension',
    'player_school_transfer_termination' = 'player_school_transfer_termination',
    'professional_player_hosting' = 'professional_player_hosting',
    'professional_player_transfer' = 'professional_player_transfer',
    'player_hosting_termination' = 'player_hosting_termination',
    'player_transfer_to_abroad' = 'player_transfer_to_abroad',
    'player_hosting_extension' = 'player_hosting_extension',
    'member_termination' = 'member_termination',
    'member_roles_termination' = 'member_roles_termination',
    'terminate_player_owner_club' = 'terminate_player_owner_club',
}

export enum PersonContractDetailPageTopActionsEnum {
    'edit-contract' = 'edit-contract',
    archive = 'archive',
    'invalidate-contract' = 'invalidate-contract',
}

export enum LeaguePageTopActionsEnum {
    'edit-league' = 'edit-league',
    archive = 'archive',
}

export enum LeaguePageDatesTopActionsEnum {
    'generate-matches' = 'generate-matches',
    'generate-dates' = 'generate-dates',
    'generate-dates-number' = 'generate-dates-number',
}

export enum CompetitionMatchesPageTopActionsEnum {
    'new-match' = 'new-match',
    'generate-dates-number' = 'generate-dates-number',
    'generate-matches' = 'generate-matches',
}

export interface PageConfigInterface {
    actions: PageTopActionsConfigInterface;
    modules: {
        [key: string]: ConfigBlockInterface;
    };
}

export interface PlayerInterface extends PersonInterface {
    passports?: PassportInterface[]; // TODO paging
}

export interface PersonBaseInterface {
    profileColors: string[];
    person: PersonDetailsApiInterface;
    member?: MemberDetailApiInterface;
    player?: PlayerDetailApiInterface;
    playerIndices?: TagApiInterface[];
    memberIndices: TagApiInterface[];
    discipline: DisciplineEnum;
    memberStatus: FootballStateEnum | FutsalStateEnum;
}
